import * as React from 'react';
import { Link } from 'react-router-dom';
import Modal from 'antd/lib/modal/Modal';
import { ModalProps } from 'antd/lib/modal';
import Select from 'antd/lib/select';
import Button from 'antd/lib/button';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Checkbox from 'antd/lib/checkbox';
import Tooltip from 'antd/lib/tooltip';
import { CopyOutlined, DeleteOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { TriageTicketFilterCondition, TriageTicketFilterField, TriageTicketFilterGroup } from '@/types';
import { antGrey, runwayColors, titleStyle } from '../styles/globalStyles';
import { NumericBadge } from '../automations/AutomationSettings';

export const TRIAGE_FILTER_FIELDS: { value: TriageTicketFilterField; label: string; description: string }[] = [
    {
        value: TriageTicketFilterField.IssueType,
        label: 'Issue type',
        description: 'Tickets of one of these types.',
    },
    {
        value: TriageTicketFilterField.Label,
        label: 'Label',
        description: 'Tickets carrying one of these labels. Supports {version} patterns, e.g. bug-{version}.',
    },
    {
        value: TriageTicketFilterField.TitleKeyword,
        label: 'Title keyword',
        description: 'Tickets whose title contains one of these, e.g. [iOS].',
    },
    {
        value: TriageTicketFilterField.AdditionalField,
        label: 'Additional field',
        description: 'Tickets whose Additional field matches one of these values.',
    },
];

export const triageFilterConditionLabel = (condition: TriageTicketFilterCondition) =>
    condition.field === TriageTicketFilterField.AdditionalField && condition.fieldName ?
        condition.fieldName
    :   (TRIAGE_FILTER_FIELDS.find((option) => option.value === condition.field)?.label ?? condition.field);

export const OrSeparator: React.FC = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
        <div style={dashedLineStyle} />
        <Text style={{ color: antGrey.grey8, letterSpacing: '0.1em', textTransform: 'uppercase' }}>or</Text>
        <div style={dashedLineStyle} />
    </div>
);

const dashedLineStyle: React.CSSProperties = {
    flex: 1,
    height: 1,
    backgroundImage: `repeating-linear-gradient(to right, ${antGrey.grey5} 0 2px, transparent 2px 4px)`,
};

const FIELD_SELECT_WIDTH = 150;
// Badge (24 + 2×6 margin) and row gaps, so the description lines up under the values.
const VALUES_OFFSET = 36 + 8 + FIELD_SELECT_WIDTH + 8;

// A group can hold several additional field conditions, one per field name.
const isSingleUseField = (field: TriageTicketFilterField) => field !== TriageTicketFilterField.AdditionalField;

const newCondition = (usedFields: TriageTicketFilterField[]): TriageTicketFilterCondition => ({
    field:
        TRIAGE_FILTER_FIELDS.find((option) => !usedFields.includes(option.value))?.value ??
        TriageTicketFilterField.AdditionalField,
    values: [],
});

const newGroup = (): TriageTicketFilterGroup => ({ conditions: [newCondition([])] });

const isConditionValid = (condition: TriageTicketFilterCondition) =>
    (condition.values?.length ?? 0) > 0 &&
    (condition.field !== TriageTicketFilterField.AdditionalField || !!condition.fieldName);

const isGroupValid = (group: TriageTicketFilterGroup) =>
    (group.conditions?.length ?? 0) > 0 && (group.conditions ?? []).every(isConditionValid);

interface NewTicketFilterModalProps {
    open: boolean;
    isLoading: boolean;
    groups: TriageTicketFilterGroup[];
    additionalFieldNames: string[] | undefined;
    additionalFieldsSettingsLink: React.ComponentProps<typeof Link>['to'];
    onCancel: ModalProps['onCancel'];
    onOk: (groups: TriageTicketFilterGroup[], closeNonMatchingIssues: boolean) => void;
}

const NewTicketFilterModal: React.FC<NewTicketFilterModalProps> = ({
    open,
    isLoading,
    groups,
    additionalFieldNames,
    additionalFieldsSettingsLink,
    onCancel,
    onOk,
}) => {
    const availableFields = TRIAGE_FILTER_FIELDS.filter(
        (option) => option.value !== TriageTicketFilterField.AdditionalField || additionalFieldNames !== undefined,
    );
    const initialDraft = React.useCallback(() => (groups.length > 0 ? groups : [newGroup()]), [groups]);
    const [draft, setDraft] = React.useState<TriageTicketFilterGroup[]>(initialDraft);
    const [closeNonMatching, setCloseNonMatching] = React.useState(false);

    React.useEffect(() => {
        if (open) {
            setDraft(initialDraft());
            setCloseNonMatching(false);
        }
    }, [open, initialDraft]);

    const updateGroup = (groupIndex: number, conditions: TriageTicketFilterCondition[]) =>
        setDraft((current) => current.map((group, idx) => (idx === groupIndex ? { conditions } : group)));

    const isValid = draft.length > 0 && draft.every(isGroupValid);

    return (
        <Modal
            title="Edit ticket filters"
            open={open}
            width={720}
            okText="Save"
            okButtonProps={{ loading: isLoading, disabled: !isValid }}
            onOk={() => onOk(draft, closeNonMatching)}
            onCancel={onCancel}
        >
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                A ticket is pulled into Triage when it matches all the conditions of at least one group.
            </Text>
            <Space direction="vertical" style={{ width: '100%' }} size={16}>
                {draft.map((group, groupIndex) => {
                    const conditions = group.conditions ?? [];
                    const usedFields = conditions.map((condition) => condition.field);
                    const usedAdditionalFieldNames = conditions.map((condition) => condition.fieldName);
                    return (
                        <React.Fragment key={groupIndex}>
                            {groupIndex > 0 && <OrSeparator />}
                            <div
                                style={{
                                    border: `1px solid ${antGrey.grey4}`,
                                    borderRadius: 4,
                                    background: runwayColors.white,
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '8px 12px',
                                        borderBottom: `1px solid ${antGrey.grey4}`,
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <NumericBadge value={groupIndex + 1} size={16} filled />
                                        <Text>Group {groupIndex + 1}</Text>
                                    </div>
                                    <Space size={4}>
                                        <Button
                                            type="link"
                                            icon={<CopyOutlined />}
                                            onClick={() =>
                                                setDraft((current) => [
                                                    ...current.slice(0, groupIndex + 1),
                                                    { conditions: conditions.map((c) => ({ ...c })) },
                                                    ...current.slice(groupIndex + 1),
                                                ])
                                            }
                                        />
                                        <Button
                                            type="link"
                                            danger
                                            icon={<DeleteOutlined />}
                                            disabled={draft.length === 1}
                                            onClick={() =>
                                                setDraft((current) => current.filter((_, idx) => idx !== groupIndex))
                                            }
                                        />
                                    </Space>
                                </div>
                                <div style={{ padding: 16 }}>
                                    <Text
                                        style={{ ...titleStyle, fontSize: 14, fontWeight: 400, color: antGrey.grey8 }}
                                    >
                                        match all of these conditions:
                                    </Text>
                                    <Space direction="vertical" style={{ width: '100%', marginTop: 10 }} size={12}>
                                        {conditions.map((condition, conditionIndex) => {
                                            const replaceCondition = (next: TriageTicketFilterCondition) =>
                                                updateGroup(
                                                    groupIndex,
                                                    conditions.map((c, idx) => (idx === conditionIndex ? next : c)),
                                                );
                                            return (
                                                <Space
                                                    key={`${condition.field}-${condition.fieldName ?? ''}`}
                                                    direction="vertical"
                                                    size={4}
                                                    style={{
                                                        width: '100%',
                                                        backgroundColor: antGrey.grey3,
                                                        borderRadius: 8,
                                                        padding: '12px 16px',
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                        <NumericBadge value={conditionIndex + 1} />
                                                        <Select
                                                            style={{ width: FIELD_SELECT_WIDTH, flexShrink: 0 }}
                                                            value={condition.field}
                                                            options={availableFields
                                                                .filter(
                                                                    (option) =>
                                                                        option.value === condition.field ||
                                                                        !isSingleUseField(option.value) ||
                                                                        !usedFields.includes(option.value),
                                                                )
                                                                .map(({ value, label }) => ({ value, label }))}
                                                            onChange={(field) =>
                                                                replaceCondition({
                                                                    field,
                                                                    values: condition.values,
                                                                })
                                                            }
                                                        />
                                                        {condition.field ===
                                                            TriageTicketFilterField.AdditionalField && (
                                                            <Select
                                                                style={{ width: FIELD_SELECT_WIDTH, flexShrink: 0 }}
                                                                placeholder="Select a field"
                                                                showSearch
                                                                value={condition.fieldName}
                                                                options={(additionalFieldNames ?? [])
                                                                    .filter(
                                                                        (name) =>
                                                                            name === condition.fieldName ||
                                                                            !usedAdditionalFieldNames.includes(name),
                                                                    )
                                                                    .map((name) => ({ value: name, label: name }))}
                                                                onChange={(fieldName: string) =>
                                                                    replaceCondition({ ...condition, fieldName })
                                                                }
                                                            />
                                                        )}
                                                        <Select
                                                            mode="tags"
                                                            allowClear
                                                            style={{ flex: 1, minWidth: 0 }}
                                                            tokenSeparators={[',']}
                                                            dropdownStyle={{ display: 'none' }}
                                                            placeholder="Type a value and press enter"
                                                            value={condition.values ?? []}
                                                            onChange={(values: string[]) =>
                                                                replaceCondition({ ...condition, values })
                                                            }
                                                        />
                                                        <Button
                                                            type="link"
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                            onClick={() =>
                                                                updateGroup(
                                                                    groupIndex,
                                                                    conditions.filter(
                                                                        (_, idx) => idx !== conditionIndex,
                                                                    ),
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                    <Text
                                                        style={{
                                                            display: 'block',
                                                            marginLeft: VALUES_OFFSET,
                                                            color: antGrey.grey7,
                                                            fontSize: 12,
                                                        }}
                                                    >
                                                        {condition.field === TriageTicketFilterField.AdditionalField ?
                                                            <>
                                                                Tickets whose <Text code>Additional field</Text> matches
                                                                one of these values. Configure{' '}
                                                                <Text code>Additional fields</Text> in{' '}
                                                                <Link to={additionalFieldsSettingsLink}>
                                                                    Project management settings
                                                                </Link>
                                                                .
                                                            </>
                                                        :   TRIAGE_FILTER_FIELDS.find(
                                                                (option) => option.value === condition.field,
                                                            )?.description
                                                        }
                                                    </Text>
                                                </Space>
                                            );
                                        })}
                                        <Button
                                            icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                                            disabled={
                                                additionalFieldNames === undefined &&
                                                usedFields.length === availableFields.length
                                            }
                                            onClick={() =>
                                                updateGroup(groupIndex, [...conditions, newCondition(usedFields)])
                                            }
                                        >
                                            Add condition
                                        </Button>
                                    </Space>
                                </div>
                            </div>
                        </React.Fragment>
                    );
                })}
                <Button
                    icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                    onClick={() => setDraft((current) => [...current, newGroup()])}
                >
                    Add group
                </Button>
                {!isValid && (
                    <Text type="danger">Each group needs at least one condition, and each condition a value.</Text>
                )}
                <div style={{ paddingTop: 8 }}>
                    <Checkbox checked={closeNonMatching} onChange={(e) => setCloseNonMatching(e.target.checked)}>
                        Mark open issues no longer matching as &apos;Not a priority&apos;
                        <Tooltip
                            title={
                                <>
                                    If this option is enabled, Runway will resolve Triage issues with existing tickets
                                    that no longer match your criteria as &apos;Not a priority&apos;.
                                    <ul style={{ margin: '4px 0', paddingLeft: 16 }}>
                                        <li>
                                            Issues that only contain these tickets (and no other signals) will be closed
                                        </li>
                                        <li>
                                            For issues that do contain other signals, the non-matching ticket will be
                                            split into its own issue and closed
                                        </li>
                                        <li>Issues with a fix request will not be affected</li>
                                    </ul>
                                    Issues may take a few minutes to update.
                                </>
                            }
                        >
                            <InfoCircleOutlined
                                style={{ color: antGrey.grey7, marginLeft: 8 }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                            />
                        </Tooltip>
                    </Checkbox>
                </div>
            </Space>
        </Modal>
    );
};

export default NewTicketFilterModal;
